import React, { useState, useEffect } from 'react';
import Logo from './Logo';
import NotificationsBell from './NotificationsBell';
import MileagePrompt from './MileagePrompt';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, Phone, ChevronDown, LogOut, Car, Heart, Sparkles, BookOpen, Flame } from 'lucide-react';
import SearchBar from './SearchBar';
import { vehicleStore, useSelectedVehicle } from '../services/vehicleStore';
import { categoriesAPI, cartAPI, brandsAPI, uploadsAPI, productsAPI, articlesAPI } from '../services/api';
import { useAlertsBadge } from '../services/alertsBadge';
import { subcategoryPath, topSubcategories, childrenOf } from '../services/catalogPath';
import PromoBanner from './PromoBanner';
import WinterTyresBanner from './WinterTyresBanner';
import { brandPath } from '../services/brandLogos';
import { useWishlistIds } from '../services/wishlistStore';
import { useGarage, garageStore } from '../services/garageStore';
import { useShippingSummary, fmtEuro } from '../services/shippingInfo';
import { openCartDrawer } from '../services/cartDrawer';
import GarageQuickPick from './GarageQuickPick';
import VehicleBrandLogo from './VehicleBrandLogo';
import LanguageSwitcher from './LanguageSwitcher';
import MobileRecentVehicles from './MobileRecentVehicles';
import CountrySwitcher from './CountrySwitcher';
import LanguageSuggestBanner from './LanguageSuggestBanner';
import MobileTopStrip from './MobileTopStrip';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useT } from '../services/i18n';
import { catName, subName } from '../services/catalogI18n';

const Header = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const vehicle = useSelectedVehicle();
  const [cartCount, setCartCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCount, setNewCount] = useState(0);
  const [showCatMenu, setShowCatMenu] = useState(false);
  const [menuCat, setMenuCat] = useState(null);
  const [brands, setBrands] = useState([]);
  const [showBrandMenu, setShowBrandMenu] = useState(false);
  const [showGuidesMenu, setShowGuidesMenu] = useState(false);
  const [topGuides, setTopGuides] = useState([]);
  useEffect(() => { articlesAPI.top(3).then(setTopGuides).catch(() => {}); }, []);
  const [showGarageMenu, setShowGarageMenu] = useState(false);
  const wishlistIds = useWishlistIds();
  const garage = useGarage();
  const shipInfo = useShippingSummary();

  useEffect(() => {
    categoriesAPI.getAll().then(setCategories).catch(() => setCategories([]));
    productsAPI.newSummary().then((d) => setNewCount(d.week || 0)).catch(() => {});
    brandsAPI.getAll().then(list => setBrands(list.filter(b => b.product_count > 0))).catch(() => setBrands([]));
  }, []);
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const alertsUnseen = useAlertsBadge(!!user);

  useEffect(() => {
    const refresh = () => {
      cartAPI.get()
        .then(cart => setCartCount((cart.items || []).reduce((n, i) => n + i.quantity, 0)))
        .catch(() => setCartCount(0));
    };
    refresh();
    window.addEventListener('cart-updated', refresh);
    return () => window.removeEventListener('cart-updated', refresh);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    garageStore.reset();
    setCartCount(0);
    navigate('/');
    setShowUserMenu(false);
  };

  const isDesktop = useMediaQuery('(min-width: 768px)');

  return (
    <header className="w-full">
      {isDesktop ? <><LanguageSuggestBanner /><PromoBanner /><WinterTyresBanner /></> : <MobileTopStrip />}
      {/* Top bar */}
      <div className="hidden md:block bg-black text-white text-sm py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/contact" data-testid="header-contact-link" className="hidden md:flex items-center gap-1 hover:text-orange-400 transition-colors">
              <Phone size={14} />
              {t('header.customerService')}
            </Link>
            <span className="text-gray-400" data-testid="header-free-shipping">{shipInfo.freeFrom != null ? t('header.freeShipping', { n: fmtEuro(shipInfo.freeFrom).replace(' €', '') }) : t('header.fastShipping')}{shipInfo.country && shipInfo.country !== 'FR' && shipInfo.countryName ? ` · ${shipInfo.countryName}` : ''}</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <CountrySwitcher />
            <span className="hidden md:inline">€ EUR</span>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-[#0e0e10] text-white">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 flex-shrink-0 min-w-0">
              <Logo variant="dark" size="xl" />
            </Link>

            {/* Search bar - desktop */}
            <SearchBar className="hidden md:block flex-1 max-w-2xl" />


            {/* Right actions */}
            <div className="flex items-center gap-3">
              {user && <div className="flex items-center" data-testid="header-notifications"><NotificationsBell user={user} /></div>}
              {/* User menu */}
              <div className="relative">
                {user ? (
                  <button 
                    className="hidden md:flex items-center gap-2 text-sm hover:text-orange-400 transition-colors"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    data-testid="header-user-button"
                  >
                    <div className="relative w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      {(user.firstName || user.first_name || 'U').charAt(0).toUpperCase()}
                      {alertsUnseen > 0 && <span data-testid="header-alerts-badge" title={t('acc.alerts')} className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#0e0e10] animate-[fadeUp_.4s_ease-out_both]">{alertsUnseen > 9 ? '9+' : alertsUnseen}</span>}
                    </div>
                    <span data-testid="header-user-name" className="hidden lg:inline">{user.firstName || user.first_name}</span>
                    <ChevronDown size={16} />
                  </button>
                ) : (
                  <button 
                    className="hidden md:flex items-center gap-2 text-sm hover:text-orange-400 transition-colors"
                    onClick={() => navigate('/connexion')}
                  >
                    <User size={20} />
                    <span className="hidden lg:inline">{t('header.login')}</span>
                  </button>
                )}

                {/* User dropdown */}
                {showUserMenu && user && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    {user.isAdmin && (
                      <Link
                        to="/admin"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowUserMenu(false)}
                      >
                        {t('header.admin')}
                      </Link>
                    )}
                    <Link
                      to="/mon-compte"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      {t('header.myAccount')}
                    </Link>
                    <Link
                      to="/mon-compte/alertes"
                      data-testid="user-menu-alerts"
                      className="flex items-center justify-between px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      {t('acc.alerts')}
                      {alertsUnseen > 0 && <span className="ml-2 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="user-menu-alerts-count">{alertsUnseen}</span>}
                    </Link>
                    <Link
                      to="/liste-de-souhaits"
                      data-testid="user-menu-wishlist"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      {t('header.myWishlist')}
                    </Link>
                    <Link
                      to="/mes-commandes"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      {t('header.myOrders')}
                    </Link>
                    <hr className="my-1" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <LogOut size={16} />
                      {t('header.logout')}
                    </button>
                  </div>
                )}
              </div>

              <Link to="/liste-de-souhaits" data-testid="header-wishlist-link" title={t('header.myWishlist')} className="relative p-2 hover:text-orange-400 transition-colors hidden sm:block">
                <Heart size={24} />
                {wishlistIds.length > 0 && (
                  <span data-testid="wishlist-count-badge" className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">
                    {wishlistIds.length}
                  </span>
                )}
              </Link>

              {/* Cart */}
              {['/panier', '/checkout'].some(p => location.pathname.startsWith(p)) ? (
                <Link to="/panier" className="relative p-2 hover:text-orange-400 transition-colors" data-testid="header-cart-link">
                  <ShoppingCart size={24} />
                  {cartCount > 0 && <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">{cartCount}</span>}
                </Link>
              ) : (
                <button type="button" onClick={() => openCartDrawer()} aria-label={t('cartDrawer.title')} className="relative p-2 hover:text-orange-400 transition-colors" data-testid="header-cart-button">
                  <ShoppingCart size={24} />
                  {cartCount > 0 && <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center">{cartCount}</span>}
                </button>
              )}

              <button 
                data-testid="mobile-menu-button"
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Search bar - mobile */}
          <div className="md:hidden mt-3">
            <SearchBar />
          </div>
        </div>

        {/* Navigation tabs */}
        <div className="border-t border-[#26262a]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center gap-1 min-w-0">
              <div className="hidden md:flex items-center gap-4 lg:gap-6 whitespace-nowrap shrink-0">
                <div
                  className="relative"
                  onMouseEnter={() => setShowCatMenu(true)}
                  onMouseLeave={() => setShowCatMenu(false)}
                >
                  <Link to="/pieces" data-testid="nav-parts-link" className="flex items-center gap-1 text-sm font-bold text-white hover:text-orange-400 transition-colors py-3">
                    {t('header.parts')}
                    <ChevronDown size={16} />
                  </Link>
                  {showCatMenu && categories.length > 0 && (() => {
                    const current = categories.find(c => c.id === menuCat) || categories[0];
                    return (
                    <div data-testid="nav-categories-menu" className="absolute left-0 top-full flex bg-white text-gray-800 rounded-b-md shadow-xl z-50 overflow-hidden">
                      <div className="w-64 py-2 border-r border-gray-100">
                      <Link to="/nouveautes" data-testid="nav-new-products" onClick={() => setShowCatMenu(false)} onMouseEnter={() => setMenuCat(null)} className="flex items-center gap-3 px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50 transition-colors border-b border-gray-100 mb-1">
                        <span className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center"><Sparkles size={15} /></span>
                        <span className="flex-1">{t('new.menu')}</span>
                        {newCount > 0 && <span className="text-[11px] font-bold bg-orange-500 text-white rounded-full px-2 py-0.5" title={t('new.menuHint', { n: newCount })} data-testid="nav-new-products-count">{newCount}</span>}
                      </Link>
                      {categories.map(c => (
                        <Link
                          key={c.id}
                          to={`/pieces/${c.slug}`}
                          data-testid={`nav-category-${c.slug}`}
                          onClick={() => setShowCatMenu(false)}
                          onMouseEnter={() => setMenuCat(c.id)}
                          className={`flex items-center gap-3 px-4 py-2 text-sm hover:bg-orange-50 hover:text-orange-600 transition-colors ${current?.id === c.id ? 'bg-orange-50 text-orange-600' : ''}`}
                        >
                          <img src={c.image} alt="" className="w-7 h-7 object-contain" />
                          <span className="flex-1">{catName(c, lang)}</span>
                          {c.subcategories.length > 0 && <ChevronDown size={14} className="-rotate-90 text-gray-400" />}
                        </Link>
                      ))}
                      </div>
                      {current?.subcategories?.length > 0 && (
                        <div className="w-72 p-3 bg-gray-50/60" data-testid={`nav-subcategories-${current.slug}`}>
                          <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide px-2 mb-1">{catName(current, lang)}</p>
                          {topSubcategories(current).map(s => (
                            <React.Fragment key={s}>
                              <Link
                                to={subcategoryPath(current, s)}
                                data-testid={`nav-subcategory-${current.slug}-${s}`}
                                onClick={() => setShowCatMenu(false)}
                                className="flex items-center gap-2.5 px-2 py-1.5 rounded text-sm hover:bg-white hover:text-orange-600 transition-colors"
                              >
                                <span className="w-9 h-9 rounded-md bg-white border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                                  {current.subcategory_images?.[s]
                                    ? <img src={uploadsAPI.resolveUrl(current.subcategory_images[s])} alt="" className="w-full h-full object-cover no-placeholder" />
                                    : <img src={current.image} alt="" className="w-6 h-6 object-contain opacity-40" />}
                                </span>
                                <span className="flex-1 truncate">{subName(current, s, lang)}</span>
                              </Link>
                              {childrenOf(current, s).length > 0 && (
                                <div className="flex flex-wrap gap-1 pl-12 pr-2 pb-1.5 -mt-0.5" data-testid={`nav-subsubcategories-${current.slug}-${s}`}>
                                  {childrenOf(current, s).map(k => (
                                    <Link key={k} to={subcategoryPath(current, k)} onClick={() => setShowCatMenu(false)} data-testid={`nav-subsubcategory-${current.slug}-${k}`} className="text-[11px] text-gray-600 bg-white border border-gray-200 rounded-full px-2 py-0.5 hover:text-orange-600 hover:border-orange-300">{subName(current, k, lang)}</Link>
                                  ))}
                                </div>
                              )}
                            </React.Fragment>
                          ))}
                          <Link to={`/pieces/${current.slug}`} onClick={() => setShowCatMenu(false)} className="block px-2 mt-2 text-xs text-blue-600 hover:underline">{t('header.wholeCategory', { name: catName(current, lang) })}</Link>
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
                {categories.slice(0, 4).map(c => (
                  <Link key={c.id} to={`/pieces/${c.slug}`} data-testid={`nav-quick-${c.slug}`} className="hidden lg:block text-sm font-bold text-white hover:text-orange-400 transition-colors">
                    {catName(c, lang)}
                  </Link>
                ))}
                <div
                  className="relative"
                  onMouseEnter={() => setShowBrandMenu(true)}
                  onMouseLeave={() => setShowBrandMenu(false)}
                >
                  <Link to="/marques" data-testid="nav-brands-link" className="flex items-center gap-1 text-sm font-bold text-white hover:text-orange-400 transition-colors py-3">
                    {t('header.brands')}
                    <ChevronDown size={16} />
                  </Link>
                  {showBrandMenu && brands.length > 0 && (
                    <div data-testid="nav-brands-menu" className="absolute left-0 top-full w-80 bg-white text-gray-800 rounded-b-md shadow-xl p-3 z-50">
                      <div className="grid grid-cols-3 gap-2">
                        {[...brands].sort((a, b) => (b.logo ? 1 : 0) - (a.logo ? 1 : 0) || b.product_count - a.product_count).slice(0, 9).map(b => (
                          <Link
                            key={b.name}
                            to={brandPath(b)}
                            data-testid={`nav-brand-${b.name}`}
                            onClick={() => setShowBrandMenu(false)}
                            title={b.name}
                            className="h-12 flex items-center justify-center border border-gray-100 rounded hover:border-orange-400 hover:bg-orange-50 transition-colors px-2"
                          >
                            {b.logo
                              ? <img src={uploadsAPI.resolveUrl(b.logo)} alt={b.name} className="max-h-7 max-w-full object-contain no-placeholder" />
                              : <span className="text-[11px] font-bold text-gray-700 truncate">{b.name}</span>}
                          </Link>
                        ))}
                      </div>
                      <Link to="/marques" data-testid="nav-brands-all" onClick={() => setShowBrandMenu(false)} className="block mt-3 text-center text-sm text-orange-600 hover:underline font-medium">
                        {t('header.allBrandsAZ', { count: brands.length })}
                      </Link>
                    </div>
                  )}
                </div>
                <div className="relative" onMouseEnter={() => setShowGuidesMenu(true)} onMouseLeave={() => setShowGuidesMenu(false)}>
                  <Link to="/articles" data-testid="nav-guides-link" className="flex items-center gap-1 text-sm font-bold text-white hover:text-orange-400 transition-colors py-3">
                    <BookOpen size={15} /> {t('header.guides')}
                    {topGuides.length > 0 && <ChevronDown size={16} />}
                  </Link>
                  {showGuidesMenu && topGuides.length > 0 && (
                    <div data-testid="nav-guides-menu" className="absolute right-0 top-full w-96 bg-white text-gray-800 rounded-b-md shadow-xl p-3 z-50">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1"><Flame size={12} className="text-orange-500" /> {t('header.topGuides')}</p>
                      <ul className="space-y-1">
                        {topGuides.map((a, i) => (
                          <li key={a.id}>
                            <Link to={`/article/${a.id}`} data-testid={`nav-guide-${i}`} onClick={() => setShowGuidesMenu(false)} className="group flex gap-3 rounded-lg p-2 hover:bg-orange-50 transition-colors">
                              {a.image ? <img src={uploadsAPI.resolveUrl(a.image)} alt="" loading="lazy" className="w-16 h-12 object-cover rounded-md flex-shrink-0 no-placeholder" /> : <span className="w-16 h-12 rounded-md bg-gray-100 flex items-center justify-center flex-shrink-0"><BookOpen size={16} className="text-gray-400" /></span>}
                              <span className="min-w-0"><span className="block text-sm font-semibold text-gray-900 group-hover:text-orange-600 leading-snug line-clamp-2">{a.title_i18n?.[lang] || a.title}</span>{a.read_time && <span className="block text-[11px] text-gray-400 mt-0.5">{a.read_time}</span>}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                      <Link to="/articles" data-testid="nav-guides-all" onClick={() => setShowGuidesMenu(false)} className="block mt-2 text-center text-sm text-orange-600 hover:underline font-medium">{t('header.allGuides')}</Link>
                    </div>
                  )}
                </div>
              </div>
              {(vehicle || (user && garage.length > 0)) && (
                <div className="ml-auto hidden md:flex items-center gap-2 relative min-w-0 shrink" data-testid="header-vehicle-chip">
                  {vehicle ? (
                    <Link
                      to="/pieces"
                      className="flex items-center gap-2 min-w-0 bg-orange-500/15 border border-orange-400/40 text-orange-100 text-xs px-3 py-1.5 rounded-full hover:bg-orange-500/25 transition-colors"
                      data-testid="header-vehicle-link"
                      title={`${vehicle.brand} ${vehicle.model}${vehicle.engine?.name ? ` · ${vehicle.engine.name}` : ''}`}
                    >
                      <VehicleBrandLogo brand={vehicle.brand} model={vehicle.model} size={22} preferModel zoom className="!border-orange-300/60 -ml-1 shrink-0" testId="header-vehicle-logo" />
                      <span className="font-medium truncate max-w-[150px] lg:max-w-[260px]">{vehicle.brand} {vehicle.model}</span>
                      {vehicle.engine?.name && <span className="text-orange-200/80 hidden lg:inline whitespace-nowrap">· {vehicle.engine.name}</span>}
                    </Link>
                  ) : (
                    <span className="flex items-center gap-2 text-gray-300 text-xs px-2 py-1.5"><Car size={14} className="text-orange-400" /> {t('garage.myVehicles')}</span>
                  )}
                  {user && garage.length > 0 && (
                    <button
                      onClick={() => setShowGarageMenu(v => !v)}
                      className={`p-1 rounded-full text-gray-300 hover:text-white hover:bg-[#1f1f23] ${showGarageMenu ? 'bg-[#1f1f23] text-white' : ''}`}
                      title={t('garage.myVehicles')}
                      data-testid="header-garage-toggle"
                      aria-expanded={showGarageMenu}
                    >
                      <ChevronDown size={14} className={`transition-transform ${showGarageMenu ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                  {vehicle && (
                    <button
                      onClick={() => vehicleStore.clear()}
                      className="p-1 rounded-full text-gray-300 hover:text-white hover:bg-[#1f1f23]"
                      title={t('header.removeVehicle')}
                      data-testid="header-vehicle-clear"
                    >
                      <X size={14} />
                    </button>
                  )}
                  {showGarageMenu && (
                    <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-testid="header-garage-menu">
                      <GarageQuickPick variant="menu" testId="header-garage-pick" onPick={() => { setShowGarageMenu(false); navigate('/pieces'); }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <MileagePrompt user={user} />

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-b shadow-lg">
          <div className="p-4 space-y-3 max-h-[75vh] overflow-y-auto">
            {user ? (
              <div className="flex flex-wrap items-center gap-2" data-testid="mobile-menu-account">
                <Link to="/mon-compte" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2.5 font-medium text-gray-900" data-testid="mobile-nav-account"><User size={18} /> {t('header.myAccount')}</Link>
                {user.isAdmin && (
                  <Link to="/admin" className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-700" data-testid="mobile-nav-admin">{t('header.admin')}</Link>
                )}
                <button onClick={handleLogout} className="inline-flex items-center justify-center rounded-lg border border-red-200 px-4 py-2.5 text-red-600" data-testid="mobile-nav-logout">{t('header.logout')}</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2" data-testid="mobile-menu-account">
                <Link to="/connexion" className="inline-flex items-center justify-center gap-2 rounded-lg bg-orange-500 px-4 py-2.5 font-semibold text-white hover:bg-orange-600" data-testid="mobile-nav-login"><User size={18} /> {t('header.login')}</Link>
                <Link to="/inscription" className="inline-flex items-center justify-center rounded-lg border border-gray-300 px-4 py-2.5 font-medium text-gray-800" data-testid="mobile-nav-register">{t('header.createAccount')}</Link>
              </div>
            )}
            <hr />
            <MobileRecentVehicles onNavigate={() => setMobileMenuOpen(false)} />
            <Link to="/pieces" className="block py-2 font-medium text-gray-900 hover:text-orange-500">{t('header.parts')}</Link>
            <Link to="/nouveautes" data-testid="mobile-nav-new-products" className="flex items-center gap-2 py-1.5 pl-3 text-orange-600 font-medium hover:text-orange-700"><Sparkles size={14} /> {t('new.menu')}{newCount > 0 && <span className="text-[11px] font-bold bg-orange-500 text-white rounded-full px-2 py-0.5">{newCount}</span>}</Link>
            {categories.map(c => (
              <Link key={c.id} to={`/pieces/${c.slug}`} className="block py-1.5 pl-3 text-gray-700 hover:text-orange-500">{catName(c, lang)}</Link>
            ))}
            <Link to="/marques" data-testid="mobile-nav-brands" className="block py-2 font-medium text-gray-900 hover:text-orange-500">{t('header.brandsAZ')}</Link>
            <Link to="/articles" data-testid="mobile-nav-guides" className="block py-2 font-medium text-gray-900 hover:text-orange-500">{t('header.guides')}</Link>
            {topGuides.map((a, i) => <Link key={a.id} to={`/article/${a.id}`} data-testid={`mobile-nav-guide-${i}`} className="block py-1.5 pl-4 text-sm text-gray-700 hover:text-orange-500 line-clamp-1">› {a.title_i18n?.[lang] || a.title}</Link>)}
            <Link to="/contact" data-testid="mobile-nav-contact" className="block py-2 font-medium text-gray-900 hover:text-orange-500">{t('header.contact')}</Link>
            <div className="pt-2" data-testid="mobile-language"><LanguageSwitcher variant="light" /></div>
            <div className="pt-2" data-testid="mobile-country"><CountrySwitcher variant="light" /></div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
